import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { ChevronDownStrokeIcon, ProfileIcon } from '@/components/core/Icon/icons'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'
import { useCookieConsent } from '@/context/CookieConsentContext'

import { MainPanel } from './MainPanel'
import { SubPanel } from './SubPanel'

import './index.scss'

const HOVER_OPEN_DELAY_MS = 80
const HOVER_CLOSE_GRACE_MS = 150

// Sub-panel slides out 4px UNDER the main panel's right edge so the two pieces
// read as a single connected surface (matches Figma — no visible seam/gap).
const SUB_PANEL_OVERLAP_PX = 4

// The sub-panel's last item (Cookie Settings) is the visual anchor: its CENTER
// aligns with the CENTER of the Privacy & Terms trigger row. Items are
// `min-height: 48px` and the panel has `padding-bottom: 4px`, so the last
// item's center sits 4 + 48/2 = 28px above the panel's bottom edge.
const SUB_PANEL_LAST_ITEM_CENTER_FROM_BOTTOM_PX = 28

// Main panel keeps a stable width regardless of sidebar collapsed/expanded
// state; matches $sidebar-width and the Figma main-panel spec.
const MAIN_PANEL_WIDTH_PX = 320

type SidebarProfileMenuProps = {
  userEmail: string
  isSidebarOpen: boolean
  onLogout: () => void
  /** Opens the account page; the menu has no "My account" item without it (the B2B portal has none). */
  onMyAccount?: () => void
  /** The current portal's Terms of Service page. */
  termsOfServicePath: string
}

export function Index({
  userEmail,
  isSidebarOpen,
  onLogout,
  onMyAccount,
  termsOfServicePath,
}: SidebarProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [subOpen, setSubOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({})
  const [subMenuStyle, setSubMenuStyle] = useState<CSSProperties>({})

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const mainPanelRef = useRef<HTMLDivElement | null>(null)
  const subPanelRef = useRef<HTMLDivElement | null>(null)
  const privacyRowRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null)

  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { openBanner } = useCookieConsent()

  const cancelOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }
  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }
  const cancelTimers = () => {
    cancelOpenTimer()
    cancelCloseTimer()
  }

  const scheduleOpen = () => {
    cancelCloseTimer()
    if (subOpen) return
    cancelOpenTimer()
    openTimerRef.current = setTimeout(() => setSubOpen(true), HOVER_OPEN_DELAY_MS)
  }

  const scheduleClose = () => {
    cancelOpenTimer()
    cancelCloseTimer()
    closeTimerRef.current = setTimeout(() => setSubOpen(false), HOVER_CLOSE_GRACE_MS)
  }

  const closeAll = () => {
    cancelTimers()
    setIsOpen(false)
    setSubOpen(false)
  }

  const toggle = () => {
    if (isOpen) {
      closeAll()
    } else {
      setIsOpen(true)
    }
  }

  const handlePrivacyClick = () => {
    cancelTimers()
    setSubOpen(prev => !prev)
  }

  const handleLogout = () => {
    closeAll()
    onLogout()
  }

  const handleMyAccount =
    typeof onMyAccount === 'undefined'
      ? undefined
      : () => {
          closeAll()
          onMyAccount()
        }

  const handleCookieSettings = () => {
    closeAll()
    openBanner()
  }

  useEffect(() => {
    if (!isOpen) {
      setSubOpen(false)
      cancelTimers()
    }
  }, [isOpen])

  useEffect(() => () => cancelTimers(), [])

  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (buttonRef.current?.contains(target)) return
      if (mainPanelRef.current?.contains(target)) return
      if (subPanelRef.current?.contains(target)) return
      closeAll()
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAll()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return

    const rect = buttonRef.current.getBoundingClientRect()
    const viewportH = window.innerHeight

    // Open upward from the sidebar's bottom-of-list trigger. Width is fixed at
    // the open-sidebar width so the popup looks identical regardless of
    // whether the sidebar is currently collapsed or expanded.
    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: MAIN_PANEL_WIDTH_PX,
      bottom: viewportH - rect.top + 8,
      zIndex: 9999,
    })
  }, [isOpen])

  useLayoutEffect(() => {
    if (!subOpen || !mainPanelRef.current || !privacyRowRef.current) return

    const mainRect = mainPanelRef.current.getBoundingClientRect()
    const privacyRect = privacyRowRef.current.getBoundingClientRect()
    const privacyCenterY = privacyRect.top + privacyRect.height / 2

    // Overlap the main panel by 4px so the surfaces look connected, and
    // anchor by `bottom` so the last item (Cookie Settings) sits at the
    // Privacy & Terms row's vertical center.
    setSubMenuStyle({
      position: 'fixed',
      left: mainRect.right - SUB_PANEL_OVERLAP_PX,
      bottom: window.innerHeight - (privacyCenterY + SUB_PANEL_LAST_ITEM_CENTER_FROM_BOTTOM_PX),
      zIndex: 9999,
    })
  }, [subOpen])

  return (
    <div className="sidebar-profile-menu">
      <button
        ref={buttonRef}
        type="button"
        className={`sidebar__bottom-item${isOpen ? ' sidebar__bottom-item--active' : ''}`}
        onClick={toggle}
        title={!isSidebarOpen ? userEmail : ''}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid={SIDEBAR_TEST_IDS.PROFILE_MENU_BUTTON}
      >
        <ProfileIcon className="sidebar__icon" />
        <span className="sidebar__nav-label sidebar__user-email">{userEmail}</span>
        <ChevronDownStrokeIcon className={`sidebar__dropdown-icon${isOpen ? ' sidebar__dropdown-icon--open' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <>
            <MainPanel
              ref={mainPanelRef}
              userEmail={userEmail}
              subOpen={subOpen}
              style={menuStyle}
              privacyRowRef={node => {
                privacyRowRef.current = node
              }}
              onPrivacyHoverEnter={scheduleOpen}
              onPrivacyHoverLeave={scheduleClose}
              onPrivacyClick={handlePrivacyClick}
              onMyAccount={handleMyAccount}
              onLogout={handleLogout}
            />
            {subOpen && (
              <SubPanel
                ref={subPanelRef}
                style={subMenuStyle}
                onMouseEnter={cancelCloseTimer}
                onMouseLeave={scheduleClose}
                onCookieSettings={handleCookieSettings}
                termsOfServicePath={termsOfServicePath}
              />
            )}
          </>,
          document.body,
        )}
    </div>
  )
}
